/**
 * runidea.ai — Phase 12 — Single-line address input with optional Google
 * Places autocomplete.
 *
 * Behaviour:
 *   - Always renders a single text input.
 *   - On mount, calls `/api/market/places-config` once. If `enabled:true`,
 *     lazy-loads the Google Maps JS SDK and attaches a Places Autocomplete
 *     widget restricted to US addresses.
 *   - Whenever the user types OR Google fires `place_changed`, we run the
 *     local regex parser so the wizard always has parsed components. Google
 *     Places fills in the value box AND emits parsed parts via place
 *     components for higher precision.
 *   - When parse confidence is `low` (no state/zip), shows the soft warning
 *     "Couldn't parse the address — autofill skipped".
 *
 * Parent contract:
 *   <SingleAddressInput
 *     value={raw}
 *     onChange={(raw, parsed) => ...}     // parsed = parseAddress output
 *     placeholder="..."
 *     testid="prop-address-full"          // optional
 *   />
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MapPin, Sparkles, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { parseAddress, formatParsedSubtitle } from "@/utils/address";

let _placesPromise = null; // module-level cache so we don't double-load

function loadGoogleMaps(key) {
  if (_placesPromise) return _placesPromise;
  // Already loaded via prior mount?
  if (typeof window !== "undefined" && window.google?.maps?.places) {
    _placesPromise = Promise.resolve(window.google);
    return _placesPromise;
  }
  _placesPromise = new Promise((resolve, reject) => {
    if (typeof document === "undefined") {
      reject(new Error("no document"));
      return;
    }
    const existing = document.querySelector('script[data-runidea-gmaps="1"]');
    if (existing) {
      existing.addEventListener("load", () => resolve(window.google));
      existing.addEventListener("error", () => reject(new Error("gmaps script failed")));
      return;
    }
    const s = document.createElement("script");
    s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&libraries=places&loading=async`;
    s.async = true;
    s.defer = true;
    s.dataset.runideaGmaps = "1";
    s.onload = () => resolve(window.google);
    s.onerror = () => {
      _placesPromise = null;
      reject(new Error("Failed to load Google Maps script"));
    };
    document.head.appendChild(s);
  });
  return _placesPromise;
}

function _componentsToParsed(place) {
  // Map Google Place components to our internal parsed shape so we can
  // bypass the regex parser when Google gives us structured data.
  const out = { street: null, unit: null, city: null, state: null, zip: null, ok: false, confidence: "high", raw: place?.formatted_address || "" };
  const comps = place?.address_components || [];
  let number = "", route = "";
  for (const c of comps) {
    const types = c.types || [];
    if (types.includes("street_number")) number = c.long_name;
    else if (types.includes("route")) route = c.long_name;
    else if (types.includes("subpremise")) out.unit = c.long_name.toLowerCase().startsWith("apt") || /\d/.test(c.long_name) ? `Apt ${c.long_name}` : c.long_name;
    else if (types.includes("locality")) out.city = c.long_name;
    else if (!out.city && types.includes("sublocality")) out.city = c.long_name;
    else if (!out.city && types.includes("neighborhood")) out.city = c.long_name;
    else if (types.includes("administrative_area_level_1")) out.state = c.short_name;
    else if (types.includes("postal_code")) out.zip = c.short_name;
  }
  out.street = [number, route].filter(Boolean).join(" ").trim() || null;
  out.ok = !!(out.state || out.zip);
  return out;
}

export default function SingleAddressInput({
  value,
  onChange,
  placeholder = "8335 SW 72nd Ave Apt 205d, Miami, FL 33143",
  testid = "prop-address-full",
  disabled = false,
  label = "Property Address",
  hint = null,
  error = null,
}) {
  const inputRef = useRef(null);
  const acRef = useRef(null);
  const [placesEnabled, setPlacesEnabled] = useState(false);
  const [parsed, setParsed] = useState(() => parseAddress(value || ""));

  // Re-parse on external value changes.
  useEffect(() => {
    setParsed((p) => (p?.raw === (value || "") ? p : parseAddress(value || "")));
  }, [value]);

  const emit = useCallback(
    (rawValue, parsedOverride) => {
      const p = parsedOverride || parseAddress(rawValue || "");
      setParsed(p);
      onChange?.(rawValue, p);
    },
    [onChange]
  );

  // Phase 12 — boot Google Places once if a key is configured. We only call
  // `/places-config` on mount; the key is only used to lazy-load the SDK.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/market/places-config");
        if (cancelled) return;
        if (!data?.enabled || !data?.key) {
          setPlacesEnabled(false);
          return;
        }
        await loadGoogleMaps(data.key);
        if (cancelled || !inputRef.current || !window.google?.maps?.places) return;
        acRef.current = new window.google.maps.places.Autocomplete(inputRef.current, {
          types: ["address"],
          componentRestrictions: { country: ["us"] },
          fields: ["address_components", "formatted_address", "geometry"],
        });
        acRef.current.addListener("place_changed", () => {
          const place = acRef.current.getPlace();
          if (!place) return;
          const raw = place.formatted_address || inputRef.current.value || "";
          // Prefer Google's structured parse; if missing fields, fall back to regex.
          const parsedGmaps = _componentsToParsed(place);
          const final = parsedGmaps.ok ? parsedGmaps : parseAddress(raw);
          emit(raw, final);
        });
        setPlacesEnabled(true);
      } catch (e) {
        // Silently fall back to regex-only — never block the wizard.
        setPlacesEnabled(false);
      }
    })();
    return () => {
      cancelled = true;
      if (acRef.current && window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(acRef.current);
      }
    };
  }, [emit]);

  const subtitle = parsed?.ok ? formatParsedSubtitle(parsed) : null;
  const warn = value && value.trim().length >= 6 && !parsed?.ok;

  return (
    <div>
      {label ? (
        <Label htmlFor={testid} className="text-slate-700">
          {label}
          {placesEnabled ? (
            <span
              data-testid={`${testid}-places-on`}
              title="Google Places autocomplete active"
              className="ml-2 inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-medium text-indigo-700"
            >
              <Sparkles className="h-2.5 w-2.5" />
              Autocomplete
            </span>
          ) : null}
        </Label>
      ) : null}
      <div className="relative mt-1.5">
        <MapPin className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input
          id={testid}
          ref={inputRef}
          data-testid={testid}
          autoComplete="off"
          spellCheck="false"
          value={value || ""}
          disabled={disabled}
          placeholder={placeholder}
          onChange={(e) => emit(e.target.value)}
          className="pl-9"
        />
      </div>
      {subtitle ? (
        <p
          data-testid={`${testid}-subtitle`}
          className="mt-1 text-xs text-slate-500"
        >
          {subtitle}
        </p>
      ) : null}
      {warn ? (
        <p
          data-testid={`${testid}-warning`}
          className="mt-1 flex items-start gap-1 text-xs text-amber-700"
        >
          <AlertTriangle className="mt-0.5 h-3 w-3 flex-shrink-0" />
          Couldn&apos;t parse the address — autofill will skip. You can still fill fields manually.
        </p>
      ) : null}
      {error ? <p className="mt-1 text-xs text-red-600">{error}</p> : null}
      {hint ? <p className="mt-1 text-xs text-slate-500">{hint}</p> : null}
    </div>
  );
}
